/** A preview computed on the device, for when the server cannot be reached.
 *
 *  `/v1/capture/preview` is a network call, so offline there is no preview — and
 *  without one the capture button stays disabled, which would make the offline
 *  queue unreachable and pointless. This fills that gap.
 *
 *  It is for **display only**. The raw text is what gets queued, so the server
 *  parses it authoritatively when the capture replays. If this parser and
 *  `parse_task_metadata` ever disagree, the server wins and the entry is still
 *  correct — the user just saw a slightly different preview while offline. That
 *  is the right way round, and it is why this is allowed to be an approximation.
 *
 *  Mirrors `src/tasks/metadata.rs`: split on whitespace, first `/` wins as the
 *  stream, first `#` as the project, every `@` accumulates as a context, and the
 *  sigil words are stripped from the content.
 */

import type { DisplayPreview, EntryKind } from './wire';

export function localPreview(
  normalizedText: string,
  kind: EntryKind,
  journalDate: string | null,
): DisplayPreview {
  const words: string[] = [];
  let stream: string | null = null;
  let project: string | null = null;
  const contexts: string[] = [];

  // The leading marker and its space have already been normalized away by the
  // caller, but be defensive: strip it if present.
  const body = /^[.\-?] /.test(normalizedText) ? normalizedText.slice(2) : normalizedText;

  for (const raw of body.split(/\s+/).filter(Boolean)) {
    const sigil = raw[0];
    const rest = raw.slice(1).trim();
    if ((sigil === '/' || sigil === '#' || sigil === '@') && rest) {
      if (sigil === '/' && stream === null) {
        stream = rest;
        continue;
      }
      if (sigil === '#' && project === null) {
        project = rest;
        continue;
      }
      if (sigil === '@') {
        if (!contexts.includes(rest)) contexts.push(rest);
        continue;
      }
    }
    words.push(raw);
  }

  return {
    kind,
    content: words.join(' '),
    // Offline the day is whatever the server last told us it was. Pinning it
    // still beats letting the replay resolve "today" whenever it happens.
    resolved_date: journalDate,
    // Offline with no cached journal date, neither placement is known. The
    // service never answers that way; this view model may.
    resolved_month: null,
    stream,
    project,
    contexts,
    priority: false,
  };
}
